import '../styles/details.css';

import { trackLead } from '../utils/analytics';
import { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import firebase from '../../src/components/firebase';
import { AuthContext } from '../services/contexts/AuthContext';
import { CartContext } from '../services/contexts/CartContext';
import { useCurrency } from '../services/contexts/CurrencyContext';
import { resolveSizeGroups } from '../utils/productSizes';
import { toSlug } from '../utils/categorySlug';
import CATEGORY_SEO from '../data/categorySeo.json';
import { Sparkle, Cloud, Heart } from './Doodles';
import { ProductImage } from './ProductImage';
import { cdnImage } from '../utils/cdnImage';
import { readEmbedded } from '../utils/embedded';

const SET_LABELS = { men: 'Мъже', women: 'Жени', kids: 'Дете' };

/* Вградени SVG икони — без зависимост от външен CDN, рисуват се веднага. */
const Icon = ({ path, size = 18 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
    fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
  >
    {path}
  </svg>
);

const ICON_CHAT = <><path d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4 20l1.3-4.2A7.5 7.5 0 1 1 20 12Z" /></>;
const ICON_PHONE = <><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" /></>;

/* Не всички пишат в чата — на телефона много хора предпочитат Viber или
   обаждане. Същият номер е във футъра. */
const PHONE = '+359893231708';
const PHONE_TEXT = '+359 893 231 708';
const VIBER_URL = `viber://chat?number=${encodeURIComponent(PHONE)}`;

/* viber:// и tel: не правят нищо, ако на устройството няма приложение за
   тях (най-често компютър без Viber) — браузърът само пише грешка в
   конзолата. Ако до секунда и половина страницата не е загубила фокуса,
   приемаме, че приложение не се е отворило, и показваме номера. */
const openApp = (url, onFail) => {
  let left = false;
  const mark = () => { left = true; };
  window.addEventListener('blur', mark);
  document.addEventListener('visibilitychange', mark);
  window.location.href = url;
  setTimeout(() => {
    window.removeEventListener('blur', mark);
    document.removeEventListener('visibilitychange', mark);
    if (!left && !document.hidden) onFail();
  }, 1500);
};

/* Не всичко е рисувано. Три вида изработка — и първото обещание трябва да
   казва истината за всеки продукт:
   – декупаж: къщичките за ключове и дървените пликове за пари (пликовете
     са в „Картички“, затова се познават и по името);
   – печат: щампите, чашите и коледните топки (сублимация), картичките
     (печат върху картон) и тениските с „двустранен печат“;
   – всичко останало е рисувано на ръка. */
const craftOf = product => {
  const type = (product.type || '').toLowerCase();
  const text = `${product.name || ''} ${product.description || ''}`;
  if (type === 'декупаж' || /декупаж|дървен плик|плик за пари/i.test(text)) return 'decoupage';
  if (['щампи', 'чаши', 'картички', 'коледна украса'].includes(type) ||
      /щамп|сублимац|печат|принт/i.test(text)) return 'print';
  return 'painted';
};

const CRAFT_PERK = {
  painted: 'Рисувано на ръка — няма две еднакви бройки',
  print: 'Отпечатано по ваша снимка, картинка или текст',
  decoupage: 'Изработено на ръка с техника декупаж',
};

/* Трите обещания са с приказните рисунки, не с иконки от CDN */
const perksFor = product => [
  { doodle: <Heart size={18} color="var(--sa-rose-400)" />, text: CRAFT_PERK[craftOf(product)] },
  { doodle: <Cloud width={24} color="var(--sa-sky-300)" />, text: 'Доставка с Еконт или Спиди до цяла България' },
  { doodle: <Sparkle size={18} color="var(--sa-butter-500)" />, text: 'Може да се персонализира с име или послание' },
];

/**
 * Известията се градят с DOM възли, а не с innerHTML, за да не се интерпретира
 * име на продукт като HTML. Оформлението им е в src/styles/theme.css.
 */
function showToast(title, text, variant) {
  const toast = document.createElement('div');
  toast.className = 'sa-toast' + (variant === 'error' ? ' sa-toast--error' : '');
  toast.setAttribute('role', 'status');

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const mark = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  mark.setAttribute('d', variant === 'error'
    ? 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 5v6m0 4h.01'
    : 'm20 6-11 11-5-5');
  svg.appendChild(mark);

  const body = document.createElement('div');
  const heading = document.createElement('div');
  heading.className = 'sa-toast-title';
  heading.textContent = title;
  const detail = document.createElement('div');
  detail.className = 'sa-toast-text';
  detail.textContent = text;
  body.append(heading, detail);

  toast.append(svg, body);
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('sa-toast--out');
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

/** Meta descriptions read badly when cut mid-word, so trim on a space. */
function clampDescription(text, max = 155) {
  const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return cut.slice(0, cut.lastIndexOf(' ')).replace(/[,.;:–-]$/, '') + '…';
}

export function Details() {
  const { currentUser } = useContext(AuthContext);
  const { addToCart } = useContext(CartContext);
  const userId = currentUser ? currentUser._delegate.uid : null;
  const { prodId } = useParams();
  // Страницата носи данните си (scripts/prerender.mjs → src/utils/embedded.js):
  // продуктът се рисува веднага, без да чака Firestore. Иначе до отговора
  // страницата беше празна обвивка „Stamsy Art“ и Google броеше продуктите
  // за копия. Firestore по-долу продължава да обновява данните.
  const [embedded] = useState(() => {
    const data = readEmbedded('sa-product');
    return data?.product?.id === prodId ? data : null;
  });
  const [product, setProduct] = useState(() => embedded?.product || {});
  const [quantity, setQuantity] = useState(1);
  const [contactHint, setContactHint] = useState(null);

  const openChat = () => {
    trackLead('chat');
    if (window.Tawk_API?.maximize) window.Tawk_API.maximize();
    else setContactHint('chat');
  };
  const openViber = () => {
    trackLead('viber');
    openApp(VIBER_URL, () => setContactHint('viber'));
  };
  const callUs = () => {
    trackLead('phone');
    openApp(`tel:${PHONE}`, () => setContactHint('phone'));
  };
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedSizeMen, setSelectedSizeMen] = useState('');
  const [selectedSizeWomen, setSelectedSizeWomen] = useState('');
  const [selectedSizeKids, setSelectedSizeKids] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [adjustedPrice, setAdjustedPrice] = useState(0);
  const [catalog, setCatalog] = useState(
    JSON.parse(localStorage.getItem('catalog')) || []
  );

  // Define women's t-shirt sizes with dimensions (Width x Length in cm)
  const womenSizes = [
    { value: 'XS', label: 'XS', width: 41.5, length: 62 },
    { value: 'S', label: 'S', width: 44, length: 63 },
    { value: 'M', label: 'M', width: 46.5, length: 64 },
    { value: 'L', label: 'L', width: 49, length: 65 },
    { value: 'XL', label: 'XL', width: 51.5, length: 66 },
    { value: '2XL', label: '2XL', width: 54, length: 67 }
  ];

  // Define men's t-shirt sizes with dimensions (Width x Length in cm)
  const menSizes = [
    { value: 'S', label: 'S', width: 46, length: 66.5 },
    { value: 'M', label: 'M', width: 51, length: 69 },
    { value: 'L', label: 'L', width: 56, length: 71.5 },
    { value: 'XL', label: 'XL', width: 61, length: 74 },
    { value: '2XL', label: '2XL', width: 66, length: 76.5 },
    { value: '3XL', label: '3XL', width: 73, length: 79 },
    { value: '4XL', label: '4XL', width: 78, length: 80.5 },
    { value: '5XL', label: '5XL', width: 83, length: 82 }
  ];

  // Define kids' t-shirt sizes with age ranges and heights
  const kidsSizes = [
    { value: '50', label: '50', age: '0-1 месеца', height: '45-50' },
    { value: '56', label: '56', age: '1-2 месеца', height: '51-56' },
    { value: '62', label: '62', age: '2-3 месеца', height: '57-62' },
    { value: '68', label: '68', age: '4-6 месеца', height: '63-68' },
    { value: '74', label: '74', age: '6-9 месеца', height: '69-74' },
    { value: '80', label: '80', age: '9-12 месеца', height: '75-80' },
    { value: '86', label: '86', age: '12-18 месеца', height: '81-86' },
    { value: '92', label: '92', age: '18-24 месеца', height: '87-92' },
    { value: '98', label: '98', age: '2-3 години', height: '93-98' },
    { value: '104', label: '104', age: '3-4 години', height: '99-104' },
    { value: '110', label: '110', age: '4-5 години', height: '105-111' },
    { value: '116', label: '116', age: '5-6 години', height: '112-116' },
    { value: '122', label: '122', age: '6-7 години', height: '117-122' },
    { value: '128', label: '128', age: '7-8 години', height: '123-128' },
    { value: '134', label: '134', age: '8-9 години', height: '129-134' },
    { value: '140', label: '140', age: '9-10 години', height: '135-140' },
    { value: '146', label: '146', age: '10-11 години', height: '141-146' },
    { value: '152', label: '152', age: '11-12 години', height: '147-152' },
    { value: '158', label: '158', age: '12-13 години', height: '153-158' },
    { value: '164', label: '164', age: '13-14 години', height: '159-164' },
    { value: '170', label: '170', age: '15-16 години', height: '165-170' }
  ];

  // Check if product is a women's t-shirt
  const isWomenTShirt = product.type?.toLowerCase().includes('жени');
  const isMenTShirt = product.type?.toLowerCase().includes('мъже');
  const isKidsTShirt = product.type?.toLowerCase().includes('деца');
  const isKomplekt = product.type?.toLowerCase().includes('комплект') || product.name?.toLowerCase().includes('комплект');

  // Check if product requires size selection
  const isTShirt = isWomenTShirt || isMenTShirt || isKidsTShirt || isKomplekt;

  // Get appropriate size array based on product type (not used for комплект)
  // Which size selectors this product asks for. Set per product in the admin
  // form; older products fall back to the type/name guess.
  const sizeGroups = resolveSizeGroups(product);
  const hasGroup = key => sizeGroups.some(g => g.key === key);
  const isRequired = key => sizeGroups.find(g => g.key === key)?.required !== false;
  const showSetSizes = sizeGroups.length > 1;
  const singleGroup = sizeGroups.length === 1 ? sizeGroups[0].key : null;

  const sizeChart = { men: menSizes, women: womenSizes, kids: kidsSizes };
  const availableSizes = singleGroup ? sizeChart[singleGroup] : [];

  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // Calculate price adjustment for t-shirt sizes
  const calculatePriceAdjustment = (size) => {
    // Men's t-shirt size adjustments
    if (isMenTShirt) {
      const menSizeAdjustments = {
        'S': 0,
        'M': 0,
        'L': 0,
        'XL': 2,
        '2XL': 4,
        '3XL': 6,
        '4XL': 8,
        '5XL': 10
      };
      return menSizeAdjustments[size] || 0;
    }

    // Women's t-shirt size adjustments
    if (isWomenTShirt) {
      const womenSizeAdjustments = {
        'XS': 0,
        'S': 0,
        'M': 0,
        'L': 0,
        'XL': 2,
        '2XL': 4
      };
      return womenSizeAdjustments[size] || 0;
    }

    // Kids' t-shirt size adjustments (larger sizes cost more)
    if (isKidsTShirt) {
      const kidsSizeAdjustments = {
        '50': 0, '56': 0, '62': 0, '68': 0, '74': 0, '80': 0,
        '86': 0, '92': 0, '98': 0, '104': 0, '110': 0,
        '116': 0, '122': 0, '128': 0,
        '134': 2,
        '140': 2,
        '146': 4,
        '152': 4,
        '158': 6,
        '164': 6,
        '170': 8
      };
      return kidsSizeAdjustments[size] || 0;
    }

    return 0;
  };

  // Get current price (base price + size adjustment)
  const getCurrentPrice = () => {
    const basePrice = parseFloat(product.price) || 0;
    return basePrice + adjustedPrice;
  };

  // Handle size change
  const handleSizeChange = (size) => {
    setSelectedSize(size);
    const adjustment = calculatePriceAdjustment(size);
    setAdjustedPrice(adjustment);
  };

  // Get images array (fallback to single img if images not available)
  const getProductImages = () => {
    if (product.images && product.images.length > 0) {
      return product.images;
    }
    return product.img ? [product.img] : [];
  };

  useEffect(() => {
    const getProduct = async () => {
      const db = firebase.firestore();

      try {
        const doc = await db.collection('Products').doc(prodId).get();

        if (doc.exists) {
          const productData = { id: doc.id, ...doc.data() };
          localStorage.setItem('product', JSON.stringify(productData));
          setProduct(productData);
        } else {
          console.log('No such document!');
        }
      } catch (error) {
        console.log('Error getting document:', error);
      }
    };

    getProduct();
  }, [prodId]);

  useEffect(() => {
    const getCatalog = async () => {
      const db = firebase.firestore();

      try {
        const snapshot = await db.collection('Products').get();

        const catalogData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Празен отговор (без връзка) не изтрива каталога — вж. App.js.
        if (catalogData.length) {
          setCatalog(catalogData);
          localStorage.setItem('catalog', JSON.stringify(catalogData));
        }
      } catch (error) {
        console.log('Error getting catalog:', error);
      }
    };

    getCatalog();
  }, []);

  const handleDeleteClick = async () => {
    const db = firebase.firestore();
    try {
      await db.collection('Products').doc(prodId).delete();
      alert('Продуктът е изтрит успешно!');

      // Get the updated catalog from Firebase after deleting the product
      const snapshot = await db.collection('Products').get();
      const catalogData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCatalog(catalogData);

      // Update the local storage version of the catalog
      localStorage.setItem('catalog', JSON.stringify(catalogData));

      navigate('/catalog', { replace: true });
    } catch (error) {
      console.log('Error deleting product:', error);
      alert('Грешка при изтриване на продукта. Моля, опитайте отново.');
    }
  };

  const handleAddToCart = () => {
    const picked = { men: selectedSizeMen, women: selectedSizeWomen, kids: selectedSizeKids };
    const missing = sizeGroups.filter(g => g.required && !picked[g.key]);
    if (showSetSizes && missing.length > 0) {
      showToast('Грешка!', 'Моля, изберете всички размери преди да добавите в количката', 'error');
      return;
    }

    if (singleGroup && !selectedSize) {
      showToast('Грешка!', 'Моля, изберете размер преди да добавите в количката', 'error');
      return;
    }

    // Built from whichever groups this product actually asks for, so a set of
    // a shirt and a mug no longer reports a women's size nobody chose.
    const setLabel = sizeGroups
      .filter(g => picked[g.key])
      .map(g => `${SET_LABELS[g.key]}: ${picked[g.key]}`)
      .join(', ');

    const productToAdd = {
      ...product,
      ...(showSetSizes && {
        selectedSize: setLabel,
        ...(selectedSizeMen && { selectedSizeMen }),
        ...(selectedSizeWomen && { selectedSizeWomen }),
        ...(selectedSizeKids && { selectedSizeKids }),
        name: `${product.name} (${setLabel})`,
      }),
      ...(singleGroup && {
        selectedSize,
        name: `${product.name} (Размер: ${selectedSize})`,
      }),
      ...(singleGroup && adjustedPrice > 0 && {
        price: getCurrentPrice().toFixed(2),
      }),
      ...(product.catalogNumber && { catalogNumber: product.catalogNumber }),
    };

    addToCart(productToAdd, quantity);
    showToast('Успешно добавено!', `${product.name} (${quantity} бр.)`);
  };

  const productImages = getProductImages();

  const productImage = productImages[0] || '';
  const productUrl = `https://stamsyart.bg/catalog/${prodId}`;
  const categorySlug = product.type ? toSlug(product.type) : '';
  // Must match scripts/prerender.mjs, which writes the same tags into the
  // static HTML — a mismatch would make Google see two different titles.
  // A name like „Бамби“ says nothing about the product, so the title borrows
  // the category's label: „Бамби – Пътечка за прощъпулник | Stamsy Art“.
  const categorySeo = CATEGORY_SEO[categorySlug];
  const needsLabel = categorySeo?.productLabel &&
    !new RegExp(categorySeo.productMatch, 'i').test(product.name || '');
  const pageTitle = !product.name
    ? 'Stamsy Art'
    : needsLabel
      ? `${product.name} – ${categorySeo.productLabel} | Stamsy Art`
      : `${product.name} – Stamsy Art | Ръчна изработка`;
  // Leading with the product name keeps this unique even where several products
  // share the same description — see the note in scripts/prerender.mjs.
  const pageDescription = product.name && product.description
    ? clampDescription(`${product.name} – ${product.description}`)
    : 'Ръчно рисувани дрехи и подаръци от Stamsy Art. Уникални handmade продукти с доставка в цяла България.';

  const breadcrumbTrail = [
    { name: 'Начало', path: '/' },
    { name: 'Каталог', path: '/catalog' },
    ...(categorySlug ? [{ name: product.type, path: `/categories/${categorySlug}` }] : []),
    ...(product.name ? [{ name: product.name, path: `/catalog/${prodId}` }] : []),
  ];

  // „Подобни продукти“ се въртят според мястото на продукта в категорията,
  // точно както в scripts/prerender.mjs. Иначе всички 22 пътечки показваха
  // едни и същи четири — още една причина Google да ги смята за еднакви.
  const sameType = catalog.filter(p => p.type === product.type);
  const siblings = sameType.filter(p => p.id !== prodId);
  const offset = siblings.length
    ? (sameType.findIndex(p => p.id === prodId) * 3) % siblings.length
    : 0;
  const rotated = offset >= 0
    ? [...siblings.slice(offset), ...siblings.slice(0, offset)]
    : siblings;
  // Докато каталогът не е дошъл, ползва вградените от страницата.
  const related = rotated.length ? rotated : (embedded?.related || []);
  const hasDiscountedBase = isTShirt && adjustedPrice > 0;

  const sizeSelect = (id, label, value, onChange, options, required, renderOption) => (
    <div className="product-field" key={id}>
      <label className="product-label" htmlFor={id}>
        {label}{required && <span className="product-req" aria-hidden="true"> *</span>}
      </label>
      <select
        id={id}
        className="product-select"
        value={value}
        required={required}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">{required ? 'Избери' : 'Не е необходимо'}</option>
        {options.map(size => (
          <option key={size.value} value={size.value}>{renderOption(size)}</option>
        ))}
      </select>
    </div>
  );

  const shirtOption = size => `${size.label} (${size.width} x ${size.length} см)`;
  const kidsOption = size => `${size.label} (${size.age}, ${size.height} см)`;

  return (
    <>
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={productUrl} />
      <meta property="og:type" content="product" />
      <meta property="og:url" content={productUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {productImage && <meta property="og:image" content={productImage} />}
      <meta property="og:site_name" content="Stamsy Art" />
      {product.name && (
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "image": productImages,
          "description": product.description || '',
          "sku": product.catalogNumber || product.id,
          // Без "category" — вж. бележката в scripts/prerender.mjs.
          "brand": { "@type": "Brand", "name": "Stamsy Art" },
          "offers": {
            "@type": "Offer",
            "url": productUrl,
            // Firestore stores prices in EUR — see CurrencyContext.
            "priceCurrency": "EUR",
            "price": (parseFloat(product.price) || 0).toFixed(2),
            "availability": "https://schema.org/InStock",
            "seller": { "@type": "Organization", "name": "Stamsy Art" }
          }
        })}</script>
      )}
      {product.name && (
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbTrail.map((item, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": item.name,
            "item": `https://stamsyart.bg${item.path}`,
          })),
        })}</script>
      )}
    </Helmet>

    <div className="product-page">
      <nav className="catalog-breadcrumb" aria-label="Навигация">
        <Link to="/">Начало</Link>
        <span aria-hidden="true">/</span>
        <Link to="/catalog">Каталог</Link>
        {categorySlug && (
          <>
            <span aria-hidden="true">/</span>
            <Link to={`/categories/${categorySlug}`}>{product.type}</Link>
          </>
        )}
        {product.name && (
          <>
            <span aria-hidden="true">/</span>
            <span className="product-crumb-current">{product.name}</span>
          </>
        )}
      </nav>

      <div className="product-layout">
        {/* ── Галерия ── */}
        <div className="product-gallery">
          {/* Снимката е отпечатък с бяло поле, залепен с лепенка върху
              пастелно петно — като в хероя на началната страница. Правоъгълна,
              не арка: арката режеше ъглите, а там често е името на детето. */}
          <div className="product-stage">
            <span className="product-blob" aria-hidden="true" />
            <span className="product-tape" aria-hidden="true" />
            <Sparkle size={22} className="sa-doodle product-star product-star--1 sa-twinkle" />
            <Sparkle size={14} className="sa-doodle product-star product-star--2 sa-twinkle" color="var(--sa-rose-300)" />
            <div className="sa-media product-media">
              {productImages[selectedImageIndex] ? (
                /* Цялата снимка, без изрязване — тук се разглеждат детайлите.
                   Смалена за екрана; og:image и JSON-LD горе ползват оригинала. */
                <ProductImage
                  src={productImages[selectedImageIndex]}
                  alt={product.name || ''}
                  widths={[600, 1000, 1400]}
                  sizes="(min-width: 900px) 580px, 100vw"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              ) : (
                <span className="product-media-placeholder" aria-hidden="true" />
              )}
            </div>
          </div>

          {productImages.length > 1 && (
            <div className="product-thumbs" role="group" aria-label="Снимки на продукта">
              {productImages.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={`product-thumb${selectedImageIndex === index ? ' is-active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                  aria-label={`Снимка ${index + 1} от ${productImages.length}`}
                  aria-pressed={selectedImageIndex === index}
                >
                  <img src={cdnImage(image, { w: 160, h: 160 })} alt="" loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Панел за поръчка ── */}
        <div className="product-panel">
          {product.type && (
            <Link className="sa-badge product-cat-link" to={`/categories/${categorySlug}`}>
              {product.type}
            </Link>
          )}

          <h1 className="product-title">{product.name}</h1>

          {product.catalogNumber && (
            <p className="product-sku">Каталожен №: {product.catalogNumber}</p>
          )}

          <div className="product-price-block">
            {hasDiscountedBase && (
              <span className="product-price-was">{formatPrice(product.price)}</span>
            )}
            <span className="product-price-now">
              {formatPrice(hasDiscountedBase ? getCurrentPrice() : product.price)}
            </span>
            {hasDiscountedBase && (
              <span className="product-price-note">цена за избрания размер</span>
            )}
          </div>

          <div className="product-form">
            <div className="product-field">
              <span className="product-label" id="qty-label">Брой</span>
              <div className="qty" role="group" aria-labelledby="qty-label">
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  aria-label="Намали бройката"
                >
                  −
                </button>
                <output className="qty-value" aria-live="polite">{quantity}</output>
                <button
                  type="button"
                  className="qty-btn"
                  onClick={() => setQuantity(quantity + 1)}
                  aria-label="Увеличи бройката"
                >
                  +
                </button>
              </div>
            </div>

            {/* Размери за комплект — по един избор на група */}
            {showSetSizes && (
              <>
                {hasGroup('men') && sizeSelect(
                  'size-men', 'Размер мъже', selectedSizeMen, setSelectedSizeMen,
                  menSizes, isRequired('men'), shirtOption
                )}
                {hasGroup('women') && sizeSelect(
                  'size-women', 'Размер жени', selectedSizeWomen, setSelectedSizeWomen,
                  womenSizes, isRequired('women'), shirtOption
                )}
                {hasGroup('kids') && sizeSelect(
                  'size-kids', 'Размер дете', selectedSizeKids, setSelectedSizeKids,
                  kidsSizes, isRequired('kids'), kidsOption
                )}
              </>
            )}

            {/* Размер за единичен продукт */}
            {singleGroup && sizeSelect(
              'size-single', 'Размер', selectedSize, handleSizeChange,
              availableSizes, true, singleGroup === 'kids' ? kidsOption : shirtOption
            )}

            <button
              type="button"
              className="sa-btn sa-btn--primary sa-btn--block product-cta"
              onClick={openChat}
            >
              <Icon path={ICON_CHAT} size={18} />
              Поръчай тук
            </button>
            <div className="product-cta-alt">
              <button type="button" className="sa-btn sa-btn--outline sa-btn--sm" onClick={openViber}>
                <Icon path={ICON_CHAT} size={16} />
                Viber
              </button>
              <button type="button" className="sa-btn sa-btn--outline sa-btn--sm" onClick={callUs}>
                <Icon path={ICON_PHONE} size={16} />
                Обади се
              </button>
            </div>
            <div role="status">
              {contactHint && (
                <p className="product-cta-hint">
                  {contactHint === 'viber' && 'Viber не се отвори на това устройство. '}
                  {contactHint === 'phone' && 'Обаждането не тръгна от това устройство. '}
                  {contactHint === 'chat' && 'Чатът още се зарежда. '}
                  Пишете ни във Viber или ни се обадете на{' '}
                  <strong className="product-cta-phone">{PHONE_TEXT}</strong>
                  {contactHint !== 'chat' && window.Tawk_API?.maximize && (
                    <>
                      {' '}или{' '}
                      <button type="button" className="product-cta-link" onClick={openChat}>
                        отворете чата
                      </button>
                    </>
                  )}
                  .
                </p>
              )}
            </div>
            <p className="product-cta-note">
              Пишете ни в чата, във Viber или се обадете — потвърждаваме размер, срок и доставка.
            </p>
          </div>

          {product.owner === userId && (
            <div className="product-admin">
              <Link to={`/catalog/${prodId}/edit`} className="sa-btn sa-btn--outline sa-btn--sm">
                Редактирай
              </Link>
              <button type="button" className="sa-btn sa-btn--sm product-delete" onClick={handleDeleteClick}>
                Изтрий
              </button>
            </div>
          )}

          <ul className="product-perks">
            {perksFor(product).map(({ doodle, text }) => (
              <li key={text}>
                <span className="product-perk-icon">{doodle}</span>
                {text}
              </li>
            ))}
          </ul>

          {product.description && (
            <section className="product-block">
              <h2 className="product-block-title">Описание на продукта</h2>
              <p className="product-description">{product.description}</p>
            </section>
          )}

          {product.video && (
            <section className="product-block">
              <h2 className="product-block-title">Видео</h2>
              <video className="product-video" controls preload="metadata" src={product.video} />
            </section>
          )}
        </div>
      </div>

      {/* ── Подобни продукти ── */}
      {related.length > 0 && (
        <section className="product-related">
          <div className="sa-section-head">
            <div>
              <span className="sa-section-kicker">още от същата приказка</span>
              <h2 className="sa-section-title">Подобни продукти</h2>
            </div>
            {categorySlug && (
              <Link className="sa-section-link" to={`/categories/${categorySlug}`}>Виж категорията →</Link>
            )}
          </div>
          <div className="product-related-grid">
            {related.slice(0, 4).map(p => (
              <Link
                key={p.id}
                to={`/catalog/${p.id}`}
                className="sa-card"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <div className="sa-media sa-media--cover">
                  <ProductImage
                    src={p.img}
                    alt={p.name}
                    ratio={1}
                    sizes="(min-width: 900px) 290px, 50vw"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="sa-card-body">
                  <p className="sa-name">{p.name}</p>
                  <p className="sa-price">{formatPrice(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
    </>
  );
};
